import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  isAuthenticated?: boolean;
}

const HeroSection = ({ isAuthenticated = false }: HeroSectionProps) => {
  return (
    <section className="relative bg-gradient-to-br from-primary/5 via-background to-accent/5 py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <h1 className="text-4xl lg:text-6xl font-heading font-bold text-foreground leading-tight">
                Transform Your
                <span className="text-primary block">Small Space</span>
                Into a Productive Oasis
              </h1>
              <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed">
                AI-powered optimization turns balconies, small yards, and tiny gardens into thriving spaces for food production and income generation. Get personalized plans in minutes.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                href={isAuthenticated ? "/input-form" : "/register"}
                className="inline-flex items-center justify-center px-8 py-4 bg-primary text-primary-foreground font-semibold rounded-lg hover:bg-primary/90 transition-colors duration-200 shadow-md">

                <Icon name="SparklesIcon" size={20} className="mr-2" />
                Start Optimizing Now
              </Link>
              <Link
                href="#features"
                className="inline-flex items-center justify-center px-8 py-4 border border-border text-foreground font-semibold rounded-lg hover:bg-muted transition-colors duration-200">

                Learn More
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-border">
              <div className="text-center">
                <div className="text-2xl lg:text-3xl font-bold text-primary">2.5K+</div>
                <div className="text-sm text-muted-foreground">Spaces Optimized</div>
              </div>
              <div className="text-center">
                <div className="text-2xl lg:text-3xl font-bold text-primary">$850</div>
                <div className="text-sm text-muted-foreground">Avg. Annual Income</div>
              </div>
              <div className="text-center">
                <div className="text-2xl lg:text-3xl font-bold text-primary">95%</div>
                <div className="text-sm text-muted-foreground">Success Rate</div>
              </div>
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <AppImage
                src="https://images.unsplash.com/photo-1690199304681-f3c811a01954"
                alt="Lush rooftop garden with organized vegetable beds, herbs in containers, and vertical growing systems on urban balcony"
                className="w-full h-[500px] object-cover" />

              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </div>
            
            {/* Floating Cards */}
            <div className="absolute -bottom-6 -left-6 bg-card border border-border rounded-lg p-4 shadow-lg">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-success rounded-full flex items-center justify-center">
                  <Icon name="CurrencyDollarIcon" size={20} className="text-success-foreground" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground">+$127/month</div>
                  <div className="text-xs text-muted-foreground">Income Generated</div>
                </div>
              </div>
            </div>

            <div className="absolute -top-6 -right-6 bg-card border border-border rounded-lg p-4 shadow-lg">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center">
                  <Icon name="LeafIcon" size={20} className="text-accent-foreground" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground">85% Space Used</div>
                  <div className="text-xs text-muted-foreground">Optimization Rate</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default HeroSection;